/**
 * Что именно спросить у каталога.
 *
 * Отделено от api/jamendo.ts намеренно: РЕШЕНИЕ о запросе — чистая функция и
 * живёт под тестами, а выполнение запроса — сеть. Тот же план стенд исполняет
 * по синтетическому каталогу, поэтому стенд меряет настоящую логику, а не свою
 * копию.
 *
 * Зачем вращение. Замер 2026-09-15 на живом API: одинаковый запрос возвращает
 * ровно ту же выдачу (пересечение 200 из 200), то есть без вращения продукт
 * видит одно и то же окно вечно. Стенд показал охват 3.1% каталога.
 *
 * Чем вращать, тоже замер (тег `piano`, 200 треков в окне):
 *   offset=400 → пересечение с базой 4 из 200   — двигает почти целиком
 *   boost=popularity_total → 48 из 200          — мягче, порядок по релевантности цел
 *   boost=buzzrate → 27 · listens_month → 36
 *   order=releasedate_desc → 0 из 200           — НЕ используем: сносит релевантность,
 *                                                 ради которой fuzzytags и нужен
 */
import { driftTags, queryTags } from "./profile";
import type { Profile } from "./types";
import { TAG_FREQ } from "./vocab";

export interface Query {
  tag: string;
  offset: number;
  boost: string;
}

/** Шаг окна равен лимиту выдачи: 200 треков за запрос. */
const STEP = 200;

/**
 * Глубже каталог по тегу не отдаёт. Замер глубины 2026-09-15:
 *   piano 0:200 1000:200 2000:200 4000:0 · melancholic до 1000 · chillhop и
 *   dreampop кончаются за 400.
 * Берём с запасом вниз: промахнуться в пустоту дороже, чем недобрать глубины.
 */
export const MAX_OFFSET = 600;

/** У редкого тега глубины нет вовсе — дальше первой страницы не ходим. */
const RARE_BELOW = 15;
const RARE_MAX_OFFSET = STEP;

/**
 * Замеренные варианты boost. popularity_month первый: на нулевом вращении
 * играет то же, что играло раньше, — лучшее по месяцу.
 */
export const BOOSTS = ["popularity_month", "popularity_total", "buzzrate", "listens_month"];

function maxOffsetFor(tag: string): number {
  const freq = TAG_FREQ[tag];
  return freq !== undefined && freq < RARE_BELOW ? RARE_MAX_OFFSET : MAX_OFFSET;
}

export interface PlanOpts {
  /** Номер сессии. 0 — первый подбор: начало выдачи, привычный boost. */
  rotation?: number;
  /** Сдвиг профиля для доливки радио. */
  drift?: number;
  n?: number;
}

export function planQueries(p: Profile, opts: PlanOpts = {}): Query[] {
  const { rotation = 0, drift = 0, n = 3 } = opts;
  const tags = drift ? driftTags(p, drift, n) : queryTags(p, n);
  const boost = BOOSTS[rotation % BOOSTS.length] ?? BOOSTS[0]!;

  return tags.map((tag, i) => {
    const cap = maxOffsetFor(tag);
    const steps = Math.floor(cap / STEP) + 1;
    // Ведущий тег остаётся у вершины выдачи, вглубь уходят остальные.
    //
    // Замер на стенде: когда вглубь уходят все три запроса, попадание в
    // профиль падает на 14% — у вещи с восемью тегами больше шансов попасть
    // в верхушку хоть какого-нибудь списка, поэтому лучшие совпадения
    // скучены наверху. Якорь возвращает почти всю потерю, и охват каталога
    // при этом не страдает: вглубь всё равно смотрят два запроса из трёх.
    const offset = i === 0 || rotation === 0 ? 0 : ((rotation + i) % steps) * STEP;
    return { tag, offset: Math.min(offset, cap), boost };
  });
}
